// 📁 src/CartContext.js
import React, { createContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useCurrency } from "./context/CurrencyContext";
import { API_BASE } from "./apiBase";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // 🛒 Cart (stored in localStorage)
  const [cart, setCart] = useState(() => {
    const stored = localStorage.getItem("nutrafox_cart");
    return stored ? JSON.parse(stored) : [];
  });

  const { currency, rates } = useCurrency();

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");

  // ✅ currency rate (fallback to 1 if not loaded yet)
  const rate = useMemo(() => {
    const r = rates?.[currency];
    return typeof r === "number" && !Number.isNaN(r) ? r : 1;
  }, [rates, currency]);

  // ✅ Persist cart
  useEffect(() => {
    localStorage.setItem("nutrafox_cart", JSON.stringify(cart));
  }, [cart]);

  // 🧠 Restore coupon from localStorage when currency changes
  useEffect(() => {
    const savedCode = localStorage.getItem("applied_coupon_code");
    const savedCurrency = localStorage.getItem("applied_coupon_currency");

    // only restore if coupon was applied for same currency
    if (savedCode && savedCurrency === currency) {
      setCouponCode(savedCode);
      applyCoupon(savedCode, true);
    } else {
      localStorage.removeItem("applied_coupon_code");
      localStorage.removeItem("applied_coupon_currency");
      setAppliedCoupon(null);
      setCouponError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const addToCart = (newItem) => {
    setCart((prev) => {
      const exists = prev.find((item) => item.name === newItem.name);
      if (exists) {
        return prev.map((item) =>
          item.name === newItem.name
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      const toastId = "add-to-cart";
      if (!toast.isActive(toastId)) toast.success("Item added to cart!", { toastId });

      return [...prev, { ...newItem, quantity: 1 }];
    });
  };

  const increaseQty = (name) => {
    setCart((prev) =>
      prev.map((item) =>
        item.name === name ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decreaseQty = (name) => {
    setCart((prev) => {
      const found = prev.find((i) => i.name === name);
      if (!found) return prev;

      if (found.quantity > 1) {
        return prev.map((i) =>
          i.name === name ? { ...i, quantity: i.quantity - 1 } : i
        );
      }

      // quantity === 1 → remove + single toast
      const toastId = `removed-${name}`;
      if (!toast.isActive(toastId)) toast.info("Item removed from cart", { toastId });
      return prev.filter((i) => i.name !== name);
    });
  };

  const removeItem = (name) => {
    setCart((prev) => prev.filter((item) => item.name !== name));
    const toastId = `removed-${name}`;
    if (!toast.isActive(toastId)) toast.info("Item removed from cart", { toastId });
  };

  const clearCart = () => {
    setCart([]);
    const toastId = "cart-cleared";
    if (!toast.isActive(toastId)) toast.info("Cart cleared", { toastId });
  };

  // 🧮 Subtotal in selected currency
  // Assumption: item.price is stored in INR in DB (your case)
  const cartTotal = useMemo(() => {
    return cart.reduce((acc, item) => {
      const priceInCurrency = (Number(item.price) || 0) * rate;
      return acc + priceInCurrency * (Number(item.quantity) || 0);
    }, 0);
  }, [cart, rate]);

  // 🎯 Apply coupon (forceSilent = true disables toast)
  const applyCoupon = async (code, forceSilent = false) => {
    const cleaned = String(code || "").trim().toUpperCase();
    if (!cleaned) {
      setAppliedCoupon(null);
      setCouponError("Enter a coupon code");
      return;
    }

    try {
      const res = await axios.post(`${API_BASE}/api/coupons/validate`, {
        code: cleaned,
      });

      const coupon = res.data;

      // currency mismatch
      if (coupon?.currency && coupon.currency !== currency) {
        setCouponError("Coupon does not match selected currency");
        setAppliedCoupon(null);
        if (!forceSilent) toast.error("Coupon currency mismatch");
        return;
      }

      // min cart value check (minCartValue is stored in INR)
      const minINR = Number(coupon?.minCartValue || 0);
      if (minINR > 0) {
        const minInCurrency = minINR * rate;
        if (cartTotal < minInCurrency) {
          setCouponError(
            `Minimum cart value of ${minINR} INR required for this coupon`
          );
          setAppliedCoupon(null);
          if (!forceSilent) toast.error("Cart total too low for this coupon");
          return;
        }
      }

      setAppliedCoupon(coupon);
      setCouponCode(cleaned);
      setCouponError("");

      localStorage.setItem("applied_coupon_code", cleaned);
      localStorage.setItem("applied_coupon_currency", currency);

      if (!forceSilent) toast.success(`Coupon "${cleaned}" applied`);
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError("Invalid or expired coupon");
      if (!forceSilent) toast.error("Coupon invalid or expired");
    }
  };

  // 🧠 Calculate discount in selected currency
  const discount = useMemo(() => {
    if (!appliedCoupon) return 0;

    const type = String(appliedCoupon.type || "").toLowerCase();
    const value = Number(appliedCoupon.value || 0);

    if (type === "flat") {
      // flat value stored in INR → convert to selected currency
      return value * rate;
    }

    if (type === "percentage") {
      return (cartTotal * value) / 100;
    }

    return 0;
  }, [appliedCoupon, cartTotal, rate]);

  const finalTotal = useMemo(() => Math.max(0, cartTotal - discount), [cartTotal, discount]);

  // 🚚 Delivery fee (example: free above 1000 INR, else 50 INR)
  const getDeliveryFee = () => {
    if (cart.length === 0) return 0;

    const thresholdInCurrency = 1000 * rate;
    const feeInCurrency = 50 * rate;

    return cartTotal >= thresholdInCurrency ? 0 : feeInCurrency;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addToCart,
        increaseQty,
        decreaseQty,
        removeItem,
        clearCart,
        cartTotal,
        finalTotal,
        discount,
        couponCode,
        appliedCoupon,
        couponError,
        applyCoupon,
        setCouponCode,
        setAppliedCoupon,
        getDeliveryFee,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
